import { Button } from './ui/button';
import { ArrowLeft } from 'lucide-react';
import { PathkitLogo } from './PathkitLogo';

export function AIDataUse() {
  const lastUpdated = "October 25, 2026";

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <PathkitLogo />
              <span className="text-xl">Pathkit</span>
            </div>
            <Button variant="ghost" onClick={() => window.location.href = '/'}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-lg border border-gray-200 p-8 md:p-12">
          <h1 className="text-4xl mb-4">PathKit AI Data Use</h1>
          <p className="text-gray-600 mb-2">Last Updated: {lastUpdated}</p>
          <p className="text-gray-700 mb-8">
            This page explains exactly what data PathKit sends to AI providers, what those providers do
            (and do not do) with it, and the controls you have. For our broader privacy practices, see the{' '}
            <a href="/privacy" className="text-blue-600 hover:underline">Privacy Policy</a>.
          </p>

          <div className="prose prose-gray max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl mb-4">1. AI Provider</h2>
              <p className="text-gray-700 mb-4">
                PathKit uses <strong>OpenAI</strong> as its only AI provider. AI calls are made server-side
                from our Supabase Edge Function backend; your browser never talks to OpenAI directly. We do
                not use Anthropic, Google Gemini, AWS Bedrock, Azure OpenAI, or any other AI provider at this time.
              </p>
              <p className="text-gray-700">
                Specifically we use OpenAI's <em>Chat Completions</em> API for text generation and the
                <em> Whisper</em> API for transcription of meeting audio when you enable it.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl mb-4">2. What We Send to AI</h2>
              <p className="text-gray-700 mb-4">
                AI calls are made on a per-feature basis. The data sent depends on which feature you use:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li><strong>SOW analysis &amp; suggestions:</strong> the SOW text you upload or paste</li>
                <li><strong>Discovery question generation:</strong> your discovery answers and project context</li>
                <li><strong>HLO &amp; BDM extraction:</strong> notes you paste into the BDM intake</li>
                <li><strong>Attachment processing:</strong> contents of files you attach to a project</li>
                <li><strong>Meeting notes:</strong> meeting audio (Whisper) and the resulting transcripts</li>
                <li><strong>Chat assistant:</strong> the messages you type and the names of stakeholders referenced in the chat context</li>
              </ul>
              <p className="text-gray-700">
                We do not send your billing details, passwords, session tokens, or unrelated tenant data
                to AI providers.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl mb-4">3. What We Don't Do</h2>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>
                  <strong>We do not use your data to train AI models.</strong> OpenAI's default API policy
                  prohibits training on data submitted via the API; this is the default applied to every
                  PathKit AI call. See OpenAI's{' '}
                  <a
                    href="https://openai.com/enterprise-privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    Enterprise Privacy
                  </a>{' '}
                  page for the underlying commitment.
                </li>
                <li>We do not sell or resell your data to AI providers or anyone else.</li>
                <li>We do not use your data for advertising, lookalike modeling, or analytics outside PathKit.</li>
                <li>
                  We do not use OpenAI's Assistants, Files, or Fine-tuning APIs. Each call is stateless from
                  OpenAI's perspective.
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl mb-4">4. Retention at the AI Provider</h2>
              <p className="text-gray-700 mb-4">
                Under OpenAI's default API terms, OpenAI may retain inputs and outputs for{' '}
                <strong>up to 30 days</strong> for the sole purpose of monitoring for abuse and misuse,
                after which the data is deleted. Authorized OpenAI staff may access the data only when
                legally required or to investigate misuse.
              </p>
              <p className="text-gray-700">
                If your organization requires zero retention, please contact{' '}
                <a href="mailto:privacy@pathkit.pro" className="text-blue-600 hover:underline">
                  privacy@pathkit.pro
                </a>{' '}
                so we can scope a Zero Data Retention (ZDR) arrangement.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl mb-4">5. Your Controls</h2>
              {/* "When disabled, PathKit makes no AI calls" and the AI request log retention line
                  come back once the company AI switch gates every AI feature and AI calls are
                  logged; the AI gating task restores them. */}
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>
                  <strong>Company AI switch:</strong> AI assistance can be turned off for a company
                  in <em>Company Settings</em>. The switch does not yet stop every AI feature; we are
                  extending it to all of them and will update this page when it does.
                </li>
                <li>
                  <strong>Data export:</strong> request a copy of any data PathKit holds about your
                  organization at any time.
                </li>
                <li>
                  <strong>Deletion:</strong> request deletion of project data (see the{' '}
                  <a href="/privacy" className="text-blue-600 hover:underline">Privacy Policy</a>{' '}
                  for retention specifics).
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl mb-4">6. Sub-processors Involved in AI Processing</h2>
              <p className="text-gray-700 mb-4">
                The full PathKit sub-processor list is in section 4.1 of the{' '}
                <a href="/privacy" className="text-blue-600 hover:underline">Privacy Policy</a>. Those
                relevant to AI processing are:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li><strong>OpenAI</strong> — large-language-model and transcription provider</li>
                <li><strong>Supabase</strong> — hosting and execution environment for the PathKit Edge Function that calls OpenAI</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl mb-4">7. Contact</h2>
              <p className="text-gray-700 mb-4">
                Questions about how PathKit handles your data with AI?
              </p>
              <ul className="list-none mb-4 text-gray-700 space-y-2">
                <li><strong>Email:</strong> <a href="mailto:privacy@pathkit.pro" className="text-blue-600 hover:underline">privacy@pathkit.pro</a></li>
                <li><strong>Subject line:</strong> AI Data Use — [your question]</li>
              </ul>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
